import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, generatePath } from 'react-router-dom';
import queryString from 'query-string';
import { ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { DATES_ARRAY, DATES_ID } from 'services/constants/dates-constants';
import { TFilters, TFiltersQ4Us } from 'redux/types';
import { TOrderListItem } from 'redux/reducers/orderManagement/types';
import { getOrderListRequest } from 'redux/reducers/orderManagement/reducer';
import {
	selectOrderListData,
	selectOrderListLoading,
	selectOrderListPage,
} from 'redux/reducers/orderManagement/selectors';
import { saveUrl } from 'redux/reducers/auth/reducer';
import { PATHS } from 'routes/constants';
import { tableStructure } from './tableStructure';
import { getEMerchantRequest } from '../../redux/reducers/monthlyFee/reducer';
import { getFiltersFromNavigateParams } from '../../utils/FilterParams';

export const OrderManagement: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);
	const [currentTableStructure, setCurrentTableStructure] = useState(tableStructure);

	const orderListData = useSelector(selectOrderListData);
	const orderListLoading = useSelector(selectOrderListLoading);
	const orderListPage = useSelector(selectOrderListPage);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TOrderListItem>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const handleLink = useCallback(
		(id: number) => {
			dispatch(saveUrl(window.location.pathname + window.location.search));
			navigate(generatePath(PATHS.ORDER_MANAGEMENT_DETAIL_PAGE, { orderId: String(id) }));
		},
		[dispatch, navigate],
	);

	const orderByNavigateParams = useMemo(() => {
		const orderBy: string[] = [];
		if (navigateParams?.sortIndex) {
			const sortItem = tableStructure[+navigateParams.sortIndex];
			if (sortItem) {
				orderBy.push(
					`${sortItem.selector} ${navigateParams?.sortDirection === 'desc' ? 'desc' : 'asc'}`,
				);
			}
		}
		return orderBy;
	}, [navigateParams]);

	const pageSize = PAGE_SIZE;
	const currentPage = Number(page) || 1;

	useEffect(() => {
		const navigateParamsWithoutPage = getFiltersFromNavigateParams(
			currentTableStructure,
			navigateParams,
		);

		dispatch(
			getOrderListRequest({
				tableName: 'order_info_view_with_s10',
				orderBy: orderByNavigateParams.length ? orderByNavigateParams : ['id desc'],
				filter: navigateParamsWithoutPage as TFiltersQ4Us,
				page: { size: pageSize, from: (currentPage - 1) * pageSize },
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	useEffect(() => {
		dispatch(getEMerchantRequest());
	}, [dispatch]);

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter<TOrderListItem>
				tableStructure={currentTableStructure}
				items={{
					pageNumber: currentPage,
					pageSize: pageSize,
					totalCount: orderListPage?.total ?? 0,
					data: orderListData,
				}}
				itemsIsLoading={orderListLoading}
				handleAction={() => {}}
				handleLink={handleLink}
				onTableStructureChange={handleTableStructureChange}
				emFilter
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Order Management
					</Typography>
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
